// packages/game/src/components/Bezel.ts
//
// Shared "tray" / "shelf" shape used behind the bankroll number at the top
// of the screen and behind the chip rack at the bottom. Visually links the
// two surfaces — bankroll and chips — as matching wooden trays sitting
// against the felt.
//
// Shape: a horizontal slab with the felt-facing edge curved like a shallow
// arch (bezier). The "open" edge faces the felt; the "outer" edge runs
// flush with the screen border.
import Phaser from 'phaser';

export interface BezelOptions {
  scene: Phaser.Scene;
  /** Center X of the slab in scene px. */
  centerX: number;
  /** "Outer" edge Y (top of screen for `face: 'down'`, bottom for `'up'`). */
  outerY: number;
  /** Width of the slab. */
  width: number;
  /** Total slab height (outer edge → deepest point of inner curve). */
  height: number;
  /** Which way the inner curve opens. `down` = bankroll bezel at top of
   *  screen; `up` = chip rack bezel at bottom. */
  face: 'down' | 'up';
  /** Fill color. Defaults to a dark shade darker than the felt. */
  fillColor?: number;
  /** Stroke color. Defaults to the gold table accent. */
  strokeColor?: number;
  /** Stroke width in scene px. */
  strokeWidth?: number;
}

export class Bezel {
  /** Underlying graphics object — exposed so callers can drive its scale
   *  from external animation (e.g. the bankroll bezel grows with the
   *  count-up text). */
  readonly gfx: Phaser.GameObjects.Graphics;

  constructor(opts: BezelOptions) {
    const {
      scene, centerX, outerY, width, height, face,
      fillColor = 0x1f1a14,
      strokeColor = 0xd4af37,
      strokeWidth = 2,
    } = opts;
    // Draw relative to (0, 0) and position the graphics at (centerX, outerY)
    // so external scale tweens grow the bezel symmetrically around its
    // outer-edge midpoint.
    this.gfx = scene.add.graphics();
    this.gfx.setPosition(centerX, outerY);

    const halfW = width / 2;
    const xL = -halfW;
    const xR = halfW;
    const dip = Math.round(height * 0.35);
    const dir = face === 'down' ? 1 : -1;
    const innerY = dir * height;
    const curveY = dir * (height + dip);

    this.gfx.fillStyle(fillColor, 1);
    this.gfx.lineStyle(strokeWidth, strokeColor, 1);
    this.gfx.beginPath();
    this.gfx.moveTo(xL, 0);
    this.gfx.lineTo(xR, 0);
    this.gfx.lineTo(xR, innerY);
    const segments = 24;
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const u = 1 - t;
      // Quadratic Bezier (xR, innerY) → (0, curveY) → (xL, innerY).
      const x = u * u * xR + 2 * u * t * 0 + t * t * xL;
      const y = u * u * innerY + 2 * u * t * curveY + t * t * innerY;
      this.gfx.lineTo(x, y);
    }
    this.gfx.closePath();
    this.gfx.fillPath();
    this.gfx.strokePath();
  }

  setDepth(d: number) { this.gfx.setDepth(d); }
  destroy() { this.gfx.destroy(); }
}
